import { create } from 'zustand';
import { api } from '../utils/api';
import { decrypt, encrypt, generateDEK, initializeCrypto } from '@barmetvault/crypto';
import { useAuthStore } from './authStore';
import { itemEncryptionKey } from '../utils/item-dek';

/* ── Types ────────────────────────────────────────── */
export type SecretType =
  | 'password'
  | 'api_key'
  | 'ssh_key'
  | 'certificate'
  | 'note'
  | 'database'
  | 'custom';

export type Environment = 'production' | 'staging' | 'development' | 'all';
export type Criticality = 'critical' | 'high' | 'medium' | 'low';

export interface Vault {
  id: string;
  name: string;
  description?: string;
  icon?: string;
  itemCount: number;
  environment: Environment;
  createdAt: string;
  updatedAt: string;
  // Crypto fields from API (raw buffers)
  nameEnc?: { type: string; data: number[] };
  keyEnc?: { type: string; data: number[] };
  ownerUserId?: string;
  type?: string;
  _count?: { items: number; collections?: number };
}

export interface VaultItem {
  collectionId?: string | null;
  id: string;
  vaultId: string;
  name: string;
  type: string;
  environment: string;
  criticality: string;
  username?: string;
  url?: string;
  notes?: string;
  tags: string[];
  ttl?: number;
  expiresAt?: string;
  metadata: Record<string, string>;
  createdAt: string;
  updatedAt: string;
  // Raw encrypted fields from API
  nameEnc?: { type: string; data: number[] };
  usernameEnc?: { type: string; data: number[] };
  passwordEnc?: { type: string; data: number[] };
  urlsEnc?: { type: string; data: number[] };
  notesEnc?: { type: string; data: number[] };
  status?: string;
  revision?: number;
  // Decrypted fields
  fields?: Record<string, string>;
}

export interface VaultItemHistory {
  id: string;
  itemId: string;
  action: 'created' | 'updated' | 'accessed' | 'shared' | 'rotated';
  performedBy: string;
  timestamp: string;
  details?: string;
}

export interface Area {
  id: string;
  vaultId: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  _count?: { items: number };
}

interface VaultState {
  vaults: Vault[];
  items: VaultItem[];
  areas: Area[];
  selectedVault: Vault | null;
  selectedVaultId: string | null;
  isLoading: boolean;
  error: string | null;
  vaultKey: Uint8Array | null;

  // Pagination & filtering
  totalItems: number;
  page: number;
  pageSize: number;
  searchQuery: string;
  filterType: SecretType | 'all';
  filterEnv: Environment;
  filterCriticality: Criticality | 'all';

  // Actions
  fetchVaults: () => Promise<void>;
  createVault: (name: string) => Promise<Vault>;
  renameVault: (vaultId: string, name: string) => Promise<void>;
  selectVault: (vaultId: string | null) => Promise<void>;
  fetchItems: (vaultId?: string, page?: number) => Promise<void>;
  fetchItem: (itemId: string) => Promise<VaultItem>;
  fetchSharedItem: (shareId: string, itemId: string, grantorId: string, wrappedDekBase64: string, wrappedNonceBase64: string) => Promise<VaultItem>;
  createItem: (item: Partial<VaultItem> & { fields: Record<string, string>; vaultId: string }) => Promise<VaultItem>;
  updateItem: (itemId: string, data: Partial<VaultItem> & { fields?: Record<string, string> }) => Promise<void>;
  deleteItem: (itemId: string) => Promise<void>;
  fetchAreas: (vaultId: string) => Promise<void>;
  createArea: (vaultId: string, name: string) => Promise<Area>;
  updateArea: (areaId: string, name: string) => Promise<void>;
  deleteArea: (areaId: string) => Promise<void>;
  setSearchQuery: (query: string) => void;
  setFilterType: (type: SecretType | 'all') => void;
  setFilterEnv: (env: Environment) => void;
  setFilterCriticality: (crit: Criticality | 'all') => void;
}

export function bufferToBytes(value: unknown): Uint8Array {
  if (Array.isArray(value)) return new Uint8Array(value);
  if (value && typeof value === 'object' && 'data' in value) {
    const data = (value as { data?: unknown }).data;
    if (Array.isArray(data)) return new Uint8Array(data);
  }
  return new Uint8Array();
}

export async function encryptField(value: string, key: Uint8Array): Promise<number[]> {
  await initializeCrypto();
  const result = encrypt(new TextEncoder().encode(value), key);
  return Array.from(new Uint8Array([...result.nonce, ...result.ciphertext]));
}

export async function decryptField(value: unknown, key: Uint8Array): Promise<string> {
  const packed = bufferToBytes(value);
  if (packed.length <= 40) return '';
  await initializeCrypto();
  const plaintext = decrypt(packed.slice(24), packed.slice(0, 24), key);
  return new TextDecoder().decode(plaintext);
}

/**
 * PER-ITEM DEK (BHM-CDX-DIR-083): devuelve la DEK del ítem, migrándolo desde
 * legacy (masterKey) a per-item DEK in-place y una sola vez si hiciera falta.
 * Solo se envía ciphertext al servidor; nunca plaintext ni claves.
 */
export async function ensureItemShareableDek(itemId: string): Promise<Uint8Array> {
  const masterKey = useAuthStore.getState().masterKey;
  if (!masterKey) throw new Error('Project is locked. Unlock it before sharing secrets.');

  const { data: raw } = await api.get(`/vaults/items/${itemId}`);
  const { legacy, dek } = await itemEncryptionKey(masterKey, itemId, raw.nameEnc);
  if (!legacy) return dek;

  // Migración client-side in-place: re-cifrar los campos con la per-item DEK.
  const payload: Record<string, unknown> = {};
  for (const field of ['nameEnc', 'usernameEnc', 'passwordEnc', 'urlsEnc', 'notesEnc'] as const) {
    const enc = (raw as Record<string, unknown>)[field];
    if (enc == null) continue;
    const plaintext = await decryptField(enc, masterKey);
    payload[field] = await encryptField(plaintext, dek);
  }
  await api.put(`/vaults/items/${itemId}`, payload);
  return dek;
}

function toApiItemType(type: string): string {
  const mappings: Record<string, string> = {
    password: 'LOGIN',
    ssh_key: 'SSH',
    note: 'SECURE_NOTE',
  };
  return mappings[type.toLowerCase()] ?? type.toUpperCase();
}

function fromApiItemType(type: string): string {
  const mappings: Record<string, string> = {
    LOGIN: 'password',
    SSH: 'ssh_key',
    SECURE_NOTE: 'note',
  };
  return mappings[type.toUpperCase()] ?? type.toLowerCase();
}

function toApiEnvironment(environment: string): string {
  return environment.toLowerCase() === 'all' ? 'ANY' : environment.toUpperCase();
}

function fromApiEnvironment(environment: string): string {
  return environment.toUpperCase() === 'ANY' ? 'all' : environment.toLowerCase();
}

function fallbackVaultName(type?: string): string {
  const names: Record<string, string> = {
    PERSONAL: 'Proyecto personal',
    TEAM: 'Proyecto de equipo',
    HIGH_CRITICALITY: 'Proyecto de alta criticidad',
  };
  return names[type?.toUpperCase() ?? ''] ?? 'Proyecto cifrado';
}

/* ── Store ────────────────────────────────────────── */
export const useVaultStore = create<VaultState>()((set, get) => ({
  vaults: [],
  items: [],
  areas: [],
  selectedVault: null,
  selectedVaultId: null,
  isLoading: false,
  error: null,
  vaultKey: null,
  totalItems: 0,
  page: 1,
  pageSize: 25,
  searchQuery: '',
  filterType: 'all',
  filterEnv: 'all',
  filterCriticality: 'all',

  fetchVaults: async () => {
    set({ isLoading: true, error: null });
    try {
      const { data } = await api.get('/vaults');
      // API returns a flat array of vaults
      const rawVaults: Vault[] = Array.isArray(data) ? data : (data.vaults ?? []);
      const masterKey = useAuthStore.getState().masterKey;
      const vaults = await Promise.all(rawVaults.map(async (vault) => {
        let name = '';
        if (masterKey && vault.nameEnc) {
          try {
            name = await decryptField(vault.nameEnc, masterKey);
          } catch {
            // Older vaults may contain empty legacy metadata. Show a safe label.
          }
        }
        return {
          ...vault,
          name: name || fallbackVaultName(vault.type),
        };
      }));
      set({ vaults, isLoading: false });
    } catch (err: unknown) {
      set({
        error: err instanceof Error ? err.message : 'Failed to fetch projects',
        isLoading: false,
      });
    }
  },

  createVault: async (name) => {
    const normalizedName = name.trim();
    if (!normalizedName) throw new Error('El nombre del proyecto es obligatorio.');

    const masterKey = useAuthStore.getState().masterKey;
    if (!masterKey) {
      throw new Error('Desbloquea el proyecto antes de crear uno nuevo.');
    }

    await initializeCrypto();
    const vaultKey = generateDEK();
    const wrappedVaultKey = encrypt(vaultKey, masterKey);
    const { data } = await api.post('/vaults', {
      type: 'PERSONAL',
      nameEnc: await encryptField(normalizedName, masterKey),
      keyEnc: Array.from(new Uint8Array([
        ...wrappedVaultKey.nonce,
        ...wrappedVaultKey.ciphertext,
      ])),
    });

    const vault: Vault = {
      ...data,
      name: normalizedName,
      itemCount: 0,
      environment: 'all',
      _count: data._count ?? { items: 0 },
    };
    set((state) => ({
      vaults: [vault, ...state.vaults],
      selectedVault: vault,
      selectedVaultId: vault.id,
      vaultKey,
    }));
    return vault;
  },

  renameVault: async (vaultId, name) => {
    const normalizedName = name.trim();
    if (!normalizedName) throw new Error('El nombre del proyecto es obligatorio.');
    const masterKey = useAuthStore.getState().masterKey;
    if (!masterKey) throw new Error('Desbloquea el proyecto antes de cambiar el nombre.');

    await api.put(`/vaults/${vaultId}`, {
      nameEnc: await encryptField(normalizedName, masterKey),
    });
    set((state) => {
      const update = (vault: Vault) => vault.id === vaultId ? { ...vault, name: normalizedName } : vault;
      return {
        vaults: state.vaults.map(update),
        selectedVault: state.selectedVault?.id === vaultId ? update(state.selectedVault) : state.selectedVault,
      };
    });
  },

  selectVault: async (vaultId) => {
    const vault = get().vaults.find((v) => v.id === vaultId) ?? null;
    set({ selectedVault: vault, selectedVaultId: vaultId });
  },

  fetchItems: async (vaultId, page = 1) => {
    const vid = vaultId ?? get().selectedVaultId;
    if (!vid) {
      set({ items: [], totalItems: 0 });
      return;
    }

    set({ isLoading: true, error: null });
    try {
      const params: Record<string, string | number> = {};
      const q = get().searchQuery;
      if (q) params.search = q;
      const t = get().filterType;
      if (t !== 'all') params.type = t.toUpperCase();
      const e = get().filterEnv;
      if (e !== 'all') params.environment = e.toUpperCase();
      const c = get().filterCriticality;
      if (c !== 'all') params.criticality = c.toUpperCase();

      // API: GET /vaults/:vaultId/items → returns flat array
      const { data } = await api.get(`/vaults/${vid}/items`, { params });
      const rawItems: Record<string, unknown>[] = Array.isArray(data) ? data : (data.items ?? []);
      const masterKey = useAuthStore.getState().masterKey;
      if (!masterKey) {
        throw new Error('Project is locked. Unlock it before reading secrets.');
      }

      // Map raw API items to VaultItem format
      const mappedItems: VaultItem[] = await Promise.all(rawItems.map(async (item) => {
        try {
          // PER-ITEM DEK (DIR-083): clave por ítem derivada, con fallback legacy.
          const itemKey = (await itemEncryptionKey(masterKey, item.id as string, item.nameEnc)).key;
          const fields = {
            username: await decryptField(item.usernameEnc, itemKey),
            password: await decryptField(item.passwordEnc, itemKey),
            url: await decryptField(item.urlsEnc, itemKey),
            notes: await decryptField(item.notesEnc, itemKey),
          };
          return {
            id: item.id as string,
            vaultId: item.vaultId as string,
            name: (await decryptField(item.nameEnc, itemKey)) || '[encrypted]',
            type: fromApiItemType((item.type as string) || 'LOGIN'),
            environment: fromApiEnvironment((item.environment as string) || 'ANY'),
            criticality: ((item.criticality as string) || 'MEDIUM').toLowerCase(),
            username: fields.username,
            url: fields.url,
            notes: fields.notes,
            fields,
            tags: [],
            status: item.status as string | undefined,
            revision: item.revision as number | undefined,
            createdAt: item.createdAt as string,
            updatedAt: item.updatedAt as string,
            nameEnc: item.nameEnc as VaultItem['nameEnc'],
            usernameEnc: item.usernameEnc as VaultItem['usernameEnc'],
            passwordEnc: item.passwordEnc as VaultItem['passwordEnc'],
            urlsEnc: item.urlsEnc as VaultItem['urlsEnc'],
            notesEnc: item.notesEnc as VaultItem['notesEnc'],
            metadata: {},
          };
        } catch {
          return {
            id: item.id as string,
            vaultId: item.vaultId as string,
            name: '[encrypted]',
            type: fromApiItemType((item.type as string) || 'LOGIN'),
            environment: fromApiEnvironment((item.environment as string) || 'ANY'),
            criticality: ((item.criticality as string) || 'MEDIUM').toLowerCase(),
            tags: [],
            createdAt: item.createdAt as string,
            updatedAt: item.updatedAt as string,
            metadata: {},
          };
        }
      }));

      set({
        items: mappedItems,
        totalItems: mappedItems.length,
        page,
        isLoading: false,
      });
    } catch (err: unknown) {
      set({
        error: err instanceof Error ? err.message : 'Failed to fetch items',
        isLoading: false,
        items: [],
      });
    }
  },

  fetchItem: async (itemId) => {
    const { data: item } = await api.get(`/vaults/items/${itemId}`);
    const masterKey = useAuthStore.getState().masterKey;
    if (!masterKey) {
      throw new Error('Project is locked. Unlock it before reading secrets.');
    }

    // PER-ITEM DEK (DIR-083): clave por ítem derivada, con fallback legacy.
    const itemKey = (await itemEncryptionKey(masterKey, item.id, item.nameEnc)).key;
    const fields = {
      username: await decryptField(item.usernameEnc, itemKey),
      password: await decryptField(item.passwordEnc, itemKey),
      url: await decryptField(item.urlsEnc, itemKey),
      notes: await decryptField(item.notesEnc, itemKey),
    };
    const mapped: VaultItem = {
      id: item.id,
      vaultId: item.vaultId,
      name: (await decryptField(item.nameEnc, itemKey)) || '[encrypted]',
      type: fromApiItemType(item.type || 'LOGIN'),
      environment: fromApiEnvironment(item.environment || 'ANY'),
      criticality: (item.criticality || 'MEDIUM').toLowerCase(),
      username: fields.username,
      url: fields.url,
      notes: fields.notes,
      fields,
      tags: [],
      status: typeof item.status === 'string' ? item.status : undefined,
      revision: item.revision,
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
      metadata: {},
    };

    set((state) => ({
      items: [mapped, ...state.items.filter((existing) => existing.id !== mapped.id)],
    }));
    return mapped;
  },

  fetchSharedItem: async (shareId, itemId, grantorId, wrappedDekBase64, wrappedNonceBase64) => {
    const { unwrapDekFromGrantor } = await import('../utils/sharing');

    // 1. Fetch the item ciphertext through the share-scoped endpoint
    //    (authorized by the stored share, never by a client-supplied item id)
    const { data: item } = await api.get(`/shares/${shareId}/item`);
    // Defense in depth: the share-scoped response must reference the expected item.
    if (item.id !== itemId) throw new Error('Share does not reference the requested item');

    // 2. Unwrap the DEK using ECDH
    const dek = await unwrapDekFromGrantor(wrappedDekBase64, wrappedNonceBase64, grantorId);

    // 3. Decrypt fields with the unwrapped DEK
    const decryptWithDek = async (enc: unknown): Promise<string> => {
      if (!enc) return '';
      const bytes = bufferToBytes(enc);
      if (bytes.length === 0) return '';
      try {
        // The DEK is used directly for decryption
        const { decrypt: cryptoDecrypt } = await import('@barmetvault/crypto');
        const plaintext = cryptoDecrypt(bytes.slice(24), bytes.slice(0, 24), dek);
        return new TextDecoder().decode(plaintext);
      } catch {
        return '';
      }
    };

    const fields = {
      username: await decryptWithDek(item.usernameEnc),
      password: await decryptWithDek(item.passwordEnc),
      url: await decryptWithDek(item.urlsEnc),
      notes: await decryptWithDek(item.notesEnc),
    };

    const mapped: VaultItem = {
      id: item.id,
      vaultId: item.vaultId,
      name: (await decryptWithDek(item.nameEnc)) || '[shared item]',
      type: fromApiItemType(item.type || 'LOGIN'),
      environment: fromApiEnvironment(item.environment || 'ANY'),
      criticality: (item.criticality || 'MEDIUM').toLowerCase(),
      username: fields.username,
      url: fields.url,
      notes: fields.notes,
      fields,
      tags: [],
      status: typeof item.status === 'string' ? item.status : undefined,
      revision: item.revision,
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
      metadata: { isShared: 'true', grantorId },
    };

    set((state) => ({
      items: [mapped, ...state.items.filter((existing) => existing.id !== mapped.id)],
    }));
    return mapped;
  },

  createItem: async (itemData) => {
    const { vaultId, fields, ...metadata } = itemData;
    const masterKey = useAuthStore.getState().masterKey;
    if (!masterKey) throw new Error('Project is locked. Unlock it before saving secrets.');

    const payload = {
      type: toApiItemType(metadata.type || 'password'),
      collectionId: (itemData as Record<string, unknown>).collectionId || undefined,
      nameEnc: await encryptField(metadata.name || '', masterKey),
      usernameEnc: fields.username ? await encryptField(fields.username, masterKey) : undefined,
      passwordEnc: fields.password ? await encryptField(fields.password, masterKey) : undefined,
      urlsEnc: fields.url ? await encryptField(fields.url, masterKey) : undefined,
      notesEnc: fields.notes ? await encryptField(fields.notes, masterKey) : undefined,
      criticality: (metadata.criticality || 'MEDIUM').toUpperCase(),
      environment: toApiEnvironment(metadata.environment || 'all'),
    };

    const { data } = await api.post(`/vaults/${vaultId}/items`, payload);

    const newItem: VaultItem = {
      id: data.id,
      vaultId: data.vaultId || vaultId,
      name: metadata.name || '[encrypted]',
      type: fromApiItemType((data.type as string) || 'LOGIN'),
      environment: fromApiEnvironment((data.environment as string) || 'ANY'),
      criticality: ((data.criticality as string) || 'MEDIUM').toLowerCase(),
      username: fields.username || '',
      url: fields.url || '',
      notes: fields.notes || '',
      tags: [],
      createdAt: data.createdAt || new Date().toISOString(),
      updatedAt: data.updatedAt || new Date().toISOString(),
      metadata: {},
    };

    set((s) => ({ items: [newItem, ...s.items] }));
    return newItem;
  },

  updateItem: async (itemId, itemData) => {
    const { fields, ...metadata } = itemData;
    const masterKey = useAuthStore.getState().masterKey;
    if (!masterKey) throw new Error('Project is locked. Unlock it before saving secrets.');

    const payload: Record<string, unknown> = {};
    if (metadata.name) payload.nameEnc = await encryptField(metadata.name, masterKey);
    if (fields?.username) payload.usernameEnc = await encryptField(fields.username, masterKey);
    if (fields?.password) payload.passwordEnc = await encryptField(fields.password, masterKey);
    if (fields?.url) payload.urlsEnc = await encryptField(fields.url, masterKey);
    if (fields?.notes) payload.notesEnc = await encryptField(fields.notes, masterKey);
    if (metadata.criticality) {
      payload.criticality = metadata.criticality.toUpperCase();
    }
    if (metadata.environment) {
      payload.environment = toApiEnvironment(metadata.environment);
    }
    if (metadata.vaultId) {
      payload.vaultId = metadata.vaultId;
    }

    const { data } = await api.put(`/vaults/items/${itemId}`, payload);

    set((s) => ({
      items: s.items.map((i) =>
        i.id === itemId
          ? {
              ...i,
              ...metadata,
              username: fields?.username || i.username,
              updatedAt: data.updatedAt || new Date().toISOString(),
            }
          : i,
      ),
    }));
  },

  deleteItem: async (itemId) => {
    await api.delete(`/vaults/items/${itemId}`);
    set((s) => ({ items: s.items.filter((i) => i.id !== itemId) }));
  },

  // ── Area (Collection) CRUD ──────────────────────────────────────────

  fetchAreas: async (vaultId) => {
    try {
      const { data } = await api.get(`/vaults/${vaultId}/collections`);
      set({ areas: Array.isArray(data) ? data : [] });
    } catch {
      set({ areas: [] });
    }
  },

  createArea: async (vaultId, name) => {
    const { data } = await api.post(`/vaults/${vaultId}/collections`, { name });
    const area = data as Area;
    set((s) => ({ areas: [...s.areas, area] }));
    return area;
  },

  updateArea: async (areaId, name) => {
    await api.put(`/vaults/collections/${areaId}`, { name });
    set((s) => ({
      areas: s.areas.map((a) => (a.id === areaId ? { ...a, name } : a)),
    }));
  },

  deleteArea: async (areaId) => {
    await api.delete(`/vaults/collections/${areaId}`);
    set((s) => ({
      areas: s.areas.filter((a) => a.id !== areaId),
      // Move items from deleted area to root (null collectionId)
      items: s.items.map((i) =>
        i.collectionId === areaId
          ? { ...i, collectionId: undefined }
          : i,
      ),
    }));
  },

  setSearchQuery: (query) => set({ searchQuery: query }),
  setFilterType: (type) => set({ filterType: type }),
  setFilterEnv: (env) => set({ filterEnv: env }),
  setFilterCriticality: (crit) => set({ filterCriticality: crit }),
}));
